import { useState, useEffect, useCallback } from "react";
import Simulator from "./Simulator";
import Title from "./ui/Title";
import Button from "./ui/Button";
import BACKEND_URL from "../config";
import Cookies from 'js-cookie';
import Melding from './Melding';
import { Event } from '../types';
import { IconDownload } from "@tabler/icons-react";

export interface FeedbackSummary {
    count: number;
    latestRating: number;
}

function isOperatorMelding(event: Event): boolean {
    if (event.event_type === 'ai_operator') return true;
    if (event.event_type === 'message') {
        return event.message_sender === 'human_operator' || event.message_sender === 'ai_operator';
    }
    return false;
}

const Meldings = ({eventLog, createMessage, creatingMessage, setSelectedCharacteristicsEstimationEventId, setSelectedDocumentationSearchEventId, selectedCharacteristicsEstimationEventId, selectedDocumentationSearchEventId, selectedAIOperatorEventId, setSelectedAIOperatorEventId, selectAIOperatorEvent, selectedAIOperatorSummaryEventId, setSelectedAIOperatorSummaryEventId, selectedRequestMessageEventId, selectedLastSeenEventId, setSelectedRequestMessageEventId, setSelectedLastSeenEventId, resetAllMeldingTrackingIds, isFullscreen, simulationToken, onEventCreated, showAIActions, setShowAIActions, operatorsOnly, setOperatorsOnly}: {eventLog: Event[], createMessage: (message: string) => Promise<boolean>, creatingMessage: boolean, setSelectedCharacteristicsEstimationEventId: (eventId: number) => void, setSelectedDocumentationSearchEventId: (eventId: number) => void, selectedCharacteristicsEstimationEventId: number | null, selectedDocumentationSearchEventId: number | null, selectedAIOperatorEventId: number | null, setSelectedAIOperatorEventId: (eventId: number | null) => void, selectAIOperatorEvent: (aiOperatorEventId: number, characteristicsId?: number, documentationId?: number) => void, selectedAIOperatorSummaryEventId: number | null, setSelectedAIOperatorSummaryEventId: (eventId: number | null) => void, selectedRequestMessageEventId: number | null, selectedLastSeenEventId: number | null, setSelectedRequestMessageEventId: (eventId: number | null) => void, setSelectedLastSeenEventId: (eventId: number | null) => void, resetAllMeldingTrackingIds: () => void, isFullscreen?: boolean, simulationToken?: string | null, onEventCreated?: (event: Event) => void, showAIActions: boolean, setShowAIActions: (value: boolean) => void, operatorsOnly: boolean, setOperatorsOnly: (value: boolean) => void}) => {
    const [customMeldings, setCustomMeldings] = useState('');
    const [feedbackMap, setFeedbackMap] = useState<Record<string, FeedbackSummary>>({});

    useEffect(() => {
        const ids = eventLog.map(e => e.id).filter(Boolean);
        if (ids.length === 0) return;
        fetch(`${BACKEND_URL}/vnog/my_ai_operator_feedback/?event_ids=${ids.join(',')}`, {
            credentials: 'include',
        })
            .then(res => res.ok ? res.json() : {})
            .then((data: Record<string, Array<{ rating: number }>>) => {
                const map: Record<string, FeedbackSummary> = {};
                for (const [eventId, entries] of Object.entries(data)) {
                    if (entries.length > 0) {
                        map[eventId] = { count: entries.length, latestRating: entries[0].rating };
                    }
                }
                setFeedbackMap(map);
            })
            .catch(() => {});
    }, [eventLog]);

    const handleFeedbackSubmitted = useCallback((eventId: number, rating: number) => {
        setFeedbackMap(prev => {
            const existing = prev[String(eventId)];
            return {
                ...prev,
                [String(eventId)]: {
                    count: (existing?.count ?? 0) + 1,
                    latestRating: rating,
                },
            };
        });
    }, []);

    const handleAddCustomMeldings = async () => {
        if (!customMeldings.trim() || creatingMessage) return;
        const ok = await createMessage(customMeldings.trim());
        if (ok) {
            // Clear only on successful request initiation; event will append via websocket
            setCustomMeldings('');
        }
    }

    const displayedEventLog = operatorsOnly
        ? eventLog.filter(isOperatorMelding)
        : eventLog;
    return (
        <div className="w-full h-full min-h-0 flex flex-col">
            <div className="w-full flex flex-row justify-between items-center mb-2 shrink-0">
                <Title title="Meldkamer" subtitle="Gesimuleerd & geanonimiseerd" />
                <div className="flex items-center gap-3">
                    {simulationToken && (
                        <button
                            type="button"
                            onClick={() => window.open(`/feedback_print/${simulationToken}`, '_blank')}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
                            aria-label="Download voor feedback"
                            title="Download voor feedback"
                        >
                            <IconDownload size={20} />
                        </button>
                    )}
                    <div className="flex items-center gap-2">
                        <input
                            id="operators-only"
                            className="w-5 h-5 md:w-6 md:h-6 cursor-pointer accent-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            type="checkbox"
                            checked={operatorsOnly}
                            onChange={() => setOperatorsOnly(!operatorsOnly)}
                        />
                        <label htmlFor="operators-only" className="text-sm text-gray-700 font-medium">Alleen operatorberichten</label>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            id="show-ai-actions"
                            className="w-5 h-5 md:w-6 md:h-6 cursor-pointer accent-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            type="checkbox"
                            checked={showAIActions}
                            onChange={() => setShowAIActions(!showAIActions)}
                        />
                        <label htmlFor="show-ai-actions" className="text-sm text-gray-700 font-medium">Toon AI Acties</label>
                    </div>
                </div>
            </div>

            <div className="flex flex-row gap-3 w-full items-center mb-2 shrink-0">
                <input 
                    className="flex-1 px-4 py-2 text-base border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all" 
                    type="text" 
                    value={customMeldings} 
                    onChange={(e) => setCustomMeldings(e.target.value)}
                    disabled={creatingMessage}
                    placeholder="Voeg een kladblokregel toe..."
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            handleAddCustomMeldings();
                        }
                    }}
                />
                <Button 
                    className={`px-4 py-2 rounded-lg transition-colors text-sm whitespace-nowrap ${creatingMessage ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`} 
                    onClick={handleAddCustomMeldings}
                    disabled={creatingMessage}
                >
                    {creatingMessage ? 'Versturen…' : 'Toevoegen'}
                </Button>
            </div>

            <div className={`space-y-3 flex-1 min-h-0 overflow-y-auto pr-2`}>
                {displayedEventLog.map((melding, index) => (
                    <Melding 
                        key={index} 
                        event={melding} 
                        showAIActions={showAIActions} 
                        setSelectedCharacteristicsEstimationEventId={setSelectedCharacteristicsEstimationEventId} 
                        setSelectedDocumentationSearchEventId={setSelectedDocumentationSearchEventId} 
                        selectedCharacteristicsEstimationEventId={selectedCharacteristicsEstimationEventId} 
                        selectedDocumentationSearchEventId={selectedDocumentationSearchEventId}
                        selectedAIOperatorEventId={selectedAIOperatorEventId}
                        setSelectedAIOperatorEventId={setSelectedAIOperatorEventId}
                        selectAIOperatorEvent={selectAIOperatorEvent}
                        simulationToken={simulationToken}
                        onEventCreated={onEventCreated}
                        feedback={feedbackMap[String(melding.id)]}
                        onFeedbackSubmitted={handleFeedbackSubmitted}
                    />
                ))}
            </div>
        </div>
    )
}

export default Meldings;